import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Trash } from "@phosphor-icons/react";
import { api, imgUrl, formatErr } from "@/lib/api";
import { SPORTS_LIST, TEAMLESS_SPORTS, TEAMS_BY_SPORT, OTHER_TEAM_SENTINEL } from "@/lib/teams";
import { useAuth } from "@/context/AuthContext";
import PaymentDisclaimer from "@/components/PaymentDisclaimer";

const SPORTS = SPORTS_LIST;
const GRADING_COMPANIES = ["", "PSA", "BGS", "SGC", "CGC", "HGA", "Other"];

const BLANK_FORM = {
  sport: "Baseball",
  player_name: "",
  year: "",
  brand: "",
  set_name: "",
  team: "",
  card_number: "",
  parallel: "",
  base: false,
  insert: false,
  variation: false,
  rookie: false,
  autograph: false,
  relic: false,
  numbered: false,
  serial_number: "",
  ssp: false,
  sp: false,
  relic_type: "",
  graded: false,
  grading_company: "",
  grade: "",
  cert_number: "",
  condition: "Ungraded",
  price: "",
  shipping_price: "",
  accepts_offers: false,
  notes: "",
  front_image: null,
  back_image: null,
  status: "active",
};

const STATUS_OPTIONS = [
  { value: "active",  label: "Active",  desc: "For sale — visible on browse" },
  { value: "pending", label: "Pending", desc: "Sale in progress — hidden from browse" },
  { value: "hold",    label: "On Hold", desc: "Temporarily off-market — hidden from browse" },
  { value: "sold",    label: "Sold",    desc: "Sold — hidden from browse" },
];

export default function Sell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();  // present on /listing/:id/edit
  const isEdit = Boolean(editId);
  const [saving, setSaving] = useState(false);
  const [loadingListing, setLoadingListing] = useState(isEdit);
  const [uploading, setUploading] = useState({ front: false, back: false });
  const frontRef = useRef();
  const backRef = useRef();
  // Ordered list of the current user's active/pending/sold listings (excluding
  // removed ones) for the Prev / Next nav on the edit page.
  const [queue, setQueue] = useState([]);
  const [tierStatus, setTierStatus] = useState(null);

  const [form, setForm] = useState(BLANK_FORM);
  // Team dropdown mode — when the seller picks "Other — type in name" (e.g.
  // defunct franchises like Seattle Supersonics), we render a free-text input
  // instead of the standard team select. Auto-activates on Edit if the loaded
  // listing has a team value that isn't in the standard TEAMS_BY_SPORT roster.
  const [customTeamMode, setCustomTeamMode] = useState(false);

  // Load tier status so we can show the vendor's active-listing counter + price cap.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/vendor/tier-status");
        setTierStatus(data);
      } catch { /* silent */ }
    })();
  }, []);

  // Load the existing listing when we're in edit mode.
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/listings/${editId}`);
        const l = data.listing || {};
        setForm({
          ...BLANK_FORM,
          ...Object.fromEntries(
            Object.keys(BLANK_FORM).map((k) => [k, l[k] ?? BLANK_FORM[k]])
          ),
          // Numbers stringified for input controls
          year: l.year != null ? String(l.year) : "",
          price: l.price != null ? String(l.price) : "",
          shipping_price: l.shipping_price != null ? String(l.shipping_price) : "",
        });
        // If the listing's team isn't in our standard roster, activate the
        // custom-text mode so the seller sees + can edit the free-text value.
        const teamList = TEAMS_BY_SPORT[l.sport] || [];
        setCustomTeamMode(
          !TEAMLESS_SPORTS.has(l.sport) && !!l.team && !teamList.includes(l.team)
        );
        // Ownership guard
        if (l.seller_id && user && l.seller_id !== user.id && user.role !== "admin") {
          toast.error("Not your listing");
          navigate("/my-listings");
        }
      } catch (err) {
        toast.error(formatErr(err));
        navigate("/my-listings");
      } finally { setLoadingListing(false); }
    })();
  }, [editId, isEdit, navigate, user]);

  // Build the Prev/Next queue from the user's active listings.
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get("/listings/mine");
        setQueue((data.listings || []).filter((l) => l.status !== "removed").map((l) => l.id));
      } catch { /* nav is optional — silent if it fails */ }
    })();
  }, [isEdit, editId]);

  const currentIndex = isEdit ? queue.indexOf(editId) : -1;
  const prevId = currentIndex > 0 ? queue[currentIndex - 1] : null;
  const nextId = currentIndex >= 0 && currentIndex < queue.length - 1 ? queue[currentIndex + 1] : null;

  const hasPaymentPath = user?.paypal_link || user?.cashapp_handle || user?.venmo_link || user?.accepts_credit_card;
  const isVendor = user?.is_vendor || user?.role === "admin";

  const uploadImage = async (file, which) => {
    setUploading((u) => ({ ...u, [which]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [`${which}_image`]: data.id }));
      toast.success(`${which === "front" ? "Front" : "Back"} uploaded`);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setUploading((u) => ({ ...u, [which]: false })); }
  };

  const submit = async (e, opts = {}) => {
    e?.preventDefault();
    if (!form.front_image) { toast.error("Front image required"); return; }
    if (!form.price) { toast.error("Price required"); return; }
    // Client-side tier price-cap guard so the user sees the message before
    // the backend rejects the submit.
    const priceNum = parseFloat(form.price);
    if (tierStatus && tierStatus.max_price != null && priceNum > tierStatus.max_price) {
      toast.error(`Tier ${tierStatus.tier} max list price is $${tierStatus.max_price.toFixed(2)}. Complete more sales to unlock Tier 2.`);
      return;
    }
    if (!isEdit && tierStatus && tierStatus.max_active_listings != null &&
        tierStatus.active_listings_count >= tierStatus.max_active_listings) {
      toast.error(`Tier ${tierStatus.tier} limit reached (${tierStatus.max_active_listings} active listings).`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year, 10) : null,
        price: parseFloat(form.price),
        shipping_price: form.shipping_price ? parseFloat(form.shipping_price) : 0,
        relic_type: form.relic && form.relic_type ? form.relic_type : null,
      };
      if (isEdit) {
        const { data } = await api.patch(`/listings/${editId}`, payload);
        if (opts.goNext && nextId) {
          toast.success("Saved — editing next listing");
          navigate(`/listing/${nextId}/edit`);
        } else {
          toast.success("Listing updated");
          navigate(`/listing/${data.listing.id}`);
        }
      } else {
        const { data } = await api.post("/listings", payload);
        toast.success("Listing live!");
        navigate(`/listing/${data.listing.id}`);
      }
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  if (!isVendor) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="sell-vendor-gate">
        <div className="font-mono text-xs text-[#FFD700] tracking-widest uppercase">→ Vendor Account Required</div>
        <h1 className="mt-3 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">List Cards on Hunt57</h1>
        <p className="mt-6 text-zinc-400 leading-relaxed">
          Browsing and buying is free for everyone. To <span className="text-white font-semibold">list cards for sale</span>,
          you need to upgrade your account to a vendor account.
        </p>
        <p className="mt-3 text-zinc-500 text-sm">
          Vendor accounts are invite-only or by application — enter a referral code or apply from your Account page. A payment method is required to start the 30-day free trial. Launch promo: <span className="line-through text-zinc-500">$69.99</span> <span className="text-[#FFD700] font-semibold">$49.99</span> your first year, then $69.99/yr on your anniversary. Cancel anytime.
        </p>
        <Link to="/account?upgrade=1" data-testid="sell-upgrade-cta" className="h57-btn-primary inline-block mt-8">
          Upgrade to Vendor →
        </Link>
      </div>
    );
  }

  if (!hasPaymentPath) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16" data-testid="sell-payment-path-gate">
        <div className="font-mono text-xs text-[#FF5F1F] tracking-widest uppercase text-center">→ One More Step</div>
        <h1 className="mt-3 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter text-center">Add a Buyer Payment Path</h1>
        <div className="mt-6 h57-surface p-5 border-[#FF5F1F]/30">
          <p className="text-zinc-300 leading-relaxed">
            <span className="text-white font-semibold">This is NOT your subscription card.</span> Hunt57 doesn&apos;t collect
            payment for card sales — buyers pay you <span className="text-white">directly</span> via your own PayPal,
            Cash App, or Venmo. Add at least one so buyers know where to send funds when they purchase.
          </p>
          <ul className="mt-4 text-sm text-zinc-400 space-y-1 list-disc pl-5">
            <li><span className="text-white">PayPal link</span> — e.g. paypal.me/yourname</li>
            <li><span className="text-white">Cash App</span> — e.g. $yourhandle</li>
            <li><span className="text-white">Venmo</span> — optional</li>
          </ul>
        </div>
        <div className="mt-6 text-center">
          <Link to="/account" data-testid="sell-goto-account" className="h57-btn-primary inline-block">
            Add Payment Paths →
          </Link>
        </div>
      </div>
    );
  }

  if (isEdit && loadingListing) {
    return (
      <div className="py-24 text-center font-mono text-xs uppercase tracking-widest text-zinc-500" data-testid="edit-loading">
        Loading listing…
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 md:py-8">
      <div className="font-mono text-xs text-[#FF5F1F] tracking-widest uppercase">
        → {isEdit ? "Edit Listing" : "Sell Card"}
      </div>
      <h1 className="mt-2 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">
        {isEdit ? "Edit Your Card" : "List a Card"}
      </h1>
      {!isEdit && (
        <div className="mt-3">
          <Link
            to="/sell/bulk"
            data-testid="sell-bulk-link"
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF5F1F]"
          >
            → Have a lot of cards? Bulk upload via CSV
          </Link>
        </div>
      )}

      {/* Tier limit banner (skipped for admins / when limits are lifted) */}
      {!isEdit && tierStatus && (tierStatus.max_active_listings != null || tierStatus.max_price != null) && (
        <div
          className="mt-4 p-3 border border-[#FFD700]/30 bg-[#FFD700]/5 text-sm text-zinc-300 flex items-center justify-between flex-wrap gap-2"
          data-testid="sell-tier-banner"
        >
          <div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">Tier {tierStatus.tier}</span>
            {tierStatus.max_active_listings != null && (
              <span className="ml-2">
                {tierStatus.active_listings_count}/{tierStatus.max_active_listings} active listings
              </span>
            )}
            {tierStatus.max_price != null && (
              <span className="ml-2 text-zinc-500">· Max list price ${tierStatus.max_price.toFixed(0)}</span>
            )}
          </div>
          {tierStatus.next_threshold && (
            <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest">
              {Math.max(0, tierStatus.next_threshold.sales_needed - tierStatus.qualifying_sales_count)} sales to Tier {tierStatus.next_threshold.tier}
            </div>
          )}
        </div>
      )}

      {/* Prev / Next navigation between the user's own listings while editing */}
      {isEdit && queue.length > 1 && (
        <div
          className="mt-4 flex items-center justify-between gap-3 border border-white/10 bg-[#0A0A0A]/60 px-3 py-2"
          data-testid="edit-nav-bar"
        >
          <button
            type="button"
            onClick={() => prevId && navigate(`/listing/${prevId}/edit`)}
            disabled={!prevId || saving}
            data-testid="edit-nav-prev"
            title={prevId ? "Edit previous listing" : "No previous listing"}
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FF5F1F] disabled:text-zinc-700 disabled:cursor-not-allowed transition-colors"
          >
            ← Prev
          </button>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase" data-testid="edit-nav-position">
            Editing {currentIndex + 1} of {queue.length}
          </div>
          <button
            type="button"
            onClick={() => nextId && navigate(`/listing/${nextId}/edit`)}
            disabled={!nextId || saving}
            data-testid="edit-nav-next"
            title={nextId ? "Edit next listing" : "No next listing"}
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FF5F1F] disabled:text-zinc-700 disabled:cursor-not-allowed transition-colors"
          >
            Next →
          </button>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-8">
        {/* Images */}
        <section>
          <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400 mb-4">Card Photos</h2>
          <div className="grid grid-cols-2 gap-4">
            {["front", "back"].map((which) => {
              const val = form[`${which}_image`];
              const ref = which === "front" ? frontRef : backRef;
              return (
                <div key={which}>
                  <div className="h57-label">{which === "front" ? "Front *" : "Back (optional)"}</div>
                  <div
                    onClick={() => ref.current?.click()}
                    className="aspect-[3/4] h57-surface flex items-center justify-center overflow-hidden cursor-pointer relative"
                    data-testid={`upload-${which}`}
                  >
                    {val ? (
                      <>
                        <img src={imgUrl(val, { w: 480 })} alt={which} className="w-full h-full object-cover" decoding="async" />
                        <button type="button" onClick={(e) => { e.stopPropagation(); setForm({ ...form, [`${which}_image`]: null }); }} className="absolute top-2 right-2 bg-black/80 p-1 text-red-400" data-testid={`remove-${which}`}>
                          <Trash size={16} weight="bold" />
                        </button>
                      </>
                    ) : (
                      <div className="text-center">
                        {uploading[which] ? (
                          <div className="font-mono text-xs text-[#FF5F1F] uppercase tracking-widest">Uploading…</div>
                        ) : (
                          <>
                            <Upload size={28} weight="bold" className="text-zinc-500 mx-auto" />
                            <div className="mt-2 font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Tap to upload</div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], which)} />
                </div>
              );
            })}
          </div>
        </section>

        {/* Spelling reminder — sits between image upload and text fields */}
        <div className="border border-[#FFD700]/30 bg-[#FFD700]/5 p-4 flex items-start gap-3" data-testid="sell-spelling-notice">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#FFD700] font-bold shrink-0 pt-0.5">Heads Up</div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Please ensure spelling on your card details is correct. Misspelled player names, brands, or sets may prevent
            your listing from appearing in other collectors&apos; searches — meaning fewer eyes on your hunt.
          </p>
        </div>

        {/* Basics */}
        <section className="space-y-4">
          <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400">Basics</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="h57-label">Player *</label>
              <input required data-testid="sell-player" className="h57-input" value={form.player_name} onChange={(e) => setForm({ ...form, player_name: e.target.value })} />
            </div>
            <div>
              <label className="h57-label">Sport / Category *</label>
              <select data-testid="sell-sport" className="h57-input" value={form.sport} onChange={(e) => {
                const nextSport = e.target.value;
                // Reset team when switching to a sport that doesn't include the current team choice.
                const allowed = TEAMS_BY_SPORT[nextSport] || [];
                setCustomTeamMode(false);
                setForm((f) => ({
                  ...f,
                  sport: nextSport,
                  team: TEAMLESS_SPORTS.has(nextSport) || (f.team && !allowed.includes(f.team)) ? "" : f.team,
                }));
              }}>
                {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="h57-label">Year</label>
              <input type="number" data-testid="sell-year" className="h57-input" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
            </div>
            <div>
              <label className="h57-label">Brand</label>
              <input data-testid="sell-brand" className="h57-input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Topps" />
            </div>
            <div>
              <label className="h57-label">Set</label>
              <input data-testid="sell-set" className="h57-input" value={form.set_name} onChange={(e) => setForm({ ...form, set_name: e.target.value })} placeholder="Chrome Update" />
            </div>
            {!TEAMLESS_SPORTS.has(form.sport) && (
              <div>
                <label className="h57-label">Team</label>
                <select
                  data-testid="sell-team"
                  className="h57-input"
                  value={customTeamMode ? OTHER_TEAM_SENTINEL : form.team}
                  onChange={(e) => {
                    if (e.target.value === OTHER_TEAM_SENTINEL) {
                      setCustomTeamMode(true);
                      setForm({ ...form, team: "" });
                    } else {
                      setCustomTeamMode(false);
                      setForm({ ...form, team: e.target.value });
                    }
                  }}
                >
                  <option value="">— Select a team —</option>
                  {(TEAMS_BY_SPORT[form.sport] || []).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                  <option value={OTHER_TEAM_SENTINEL}>Other — type in a name…</option>
                </select>
                {customTeamMode && (
                  <input
                    data-testid="sell-team-other"
                    className="h57-input mt-2"
                    value={form.team}
                    onChange={(e) => setForm({ ...form, team: e.target.value })}
                    placeholder="e.g. Seattle Supersonics"
                    maxLength={80}
                    autoFocus
                  />
                )}
              </div>
            )}
            <div>
              <label className="h57-label">Card #</label>
              <input data-testid="sell-card-number" className="h57-input" value={form.card_number} onChange={(e) => setForm({ ...form, card_number: e.target.value })} />
            </div>
            <div>
              <label className="h57-label">Parallel / Variation</label>
              <input data-testid="sell-parallel" className="h57-input" value={form.parallel} onChange={(e) => setForm({ ...form, parallel: e.target.value })} placeholder="Purple Refractor" />
            </div>
          </div>
        </section>

        {/* Card type flags */}
        <section>
          <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400 mb-4">
            Card Type <span className="text-zinc-500 normal-case tracking-normal font-normal ml-1">(choose all that apply)</span>
          </h2>
          <div className="flex flex-wrap gap-2">
            {[
              ["base", "Base"],
              ["insert", "Insert"],
              ["variation", "Variation"],
              ["rookie", "Rookie"],
              ["autograph", "Auto"],
              ["relic", "Relic"],
              ["numbered", "Numbered"],
              ["ssp", "SSP"],
              ["sp", "SP"],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                data-testid={`sell-flag-${k}`}
                onClick={() => setForm({ ...form, [k]: !form[k], ...(k === "relic" && form.relic ? { relic_type: "" } : {}) })}
                className={`h57-chip !py-2 !px-4 ${form[k] ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""}`}
              >{label}</button>
            ))}
          </div>
          {form.relic && (
            <div className="mt-4 max-w-sm" data-testid="sell-relic-type-section">
              <label className="h57-label">Relic Type</label>
              <select
                className="h57-input"
                data-testid="sell-relic-type"
                value={form.relic_type}
                onChange={(e) => setForm({ ...form, relic_type: e.target.value })}
              >
                <option value="">N/A</option>
                <option value="player_worn">Player Worn</option>
                <option value="game_used">Game Used</option>
              </select>
              <div className="mt-1 font-mono text-[10px] text-zinc-500 tracking-widest uppercase">Leave as N/A if unspecified</div>
            </div>
          )}
          {form.numbered && (
            <div className="mt-3 max-w-[240px]">
              <label className="h57-label">Serial (e.g. 25/50)</label>
              <input data-testid="sell-serial" className="h57-input" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} />
            </div>
          )}
        </section>

        {/* Grading */}
        <section>
          <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400 mb-4">Grading</h2>
          <label className="h57-chip !py-2 !px-4 cursor-pointer inline-flex items-center gap-2">
            <input type="checkbox" data-testid="sell-graded" checked={form.graded} onChange={(e) => setForm({ ...form, graded: e.target.checked })} className="accent-[#FF5F1F]" /> Graded (slab)
          </label>
          {form.graded && (
            <div className="mt-3 grid md:grid-cols-3 gap-4">
              <div>
                <label className="h57-label">Company</label>
                <select data-testid="sell-grading-company" className="h57-input" value={form.grading_company} onChange={(e) => setForm({ ...form, grading_company: e.target.value })}>
                  {GRADING_COMPANIES.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
                </select>
              </div>
              <div>
                <label className="h57-label">Grade</label>
                <input data-testid="sell-grade" className="h57-input" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="10" />
              </div>
              <div>
                <label className="h57-label">Cert #</label>
                <input data-testid="sell-cert" className="h57-input" value={form.cert_number} onChange={(e) => setForm({ ...form, cert_number: e.target.value })} />
              </div>
            </div>
          )}
          {!form.graded && (
            <div className="mt-3 max-w-[300px]">
              <label className="h57-label">Raw Condition</label>
              <input data-testid="sell-condition" className="h57-input" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} placeholder="Near Mint" />
            </div>
          )}
        </section>

        {/* Price + shipping */}
        <section>
          <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400 mb-4">Price & Shipping</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="h57-label">Price *</label>
              <input required type="number" step="0.01" min="0" data-testid="sell-price" className="h57-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <label className="h57-label">Shipping</label>
              <input type="number" step="0.01" min="0" data-testid="sell-shipping" className="h57-input" value={form.shipping_price} onChange={(e) => setForm({ ...form, shipping_price: e.target.value })} />
            </div>
            <div className="flex items-end">
              <label className="h57-chip !py-2 !px-4 cursor-pointer inline-flex items-center gap-2">
                <input type="checkbox" data-testid="sell-offers" checked={form.accepts_offers} onChange={(e) => setForm({ ...form, accepts_offers: e.target.checked })} className="accent-[#FF5F1F]" /> Accept offers
              </label>
            </div>
          </div>
        </section>

        {/* Listing status — edit mode only. Sold is auto-set on tx completion
            but the seller can override here (e.g. reactivate after buyer flake). */}
        {isEdit && (
          <section data-testid="sell-status-section">
            <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400 mb-4">Listing Status</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" role="radiogroup" aria-label="Listing status">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={form.status === opt.value}
                  data-testid={`sell-status-${opt.value}`}
                  onClick={() => setForm({ ...form, status: opt.value })}
                  className={`text-left p-3 border transition-colors ${
                    form.status === opt.value
                      ? "border-[#FF5F1F] bg-[#FF5F1F]/5 text-white"
                      : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                  }`}
                >
                  <div className={`font-mono text-[10px] uppercase tracking-widest ${form.status === opt.value ? "text-[#FF5F1F]" : "text-zinc-500"}`}>
                    {opt.value === form.status ? "→ " : ""}{opt.label}
                  </div>
                  <div className="mt-1 text-xs leading-snug">{opt.desc}</div>
                </button>
              ))}
            </div>
            {form.status !== "active" && (
              <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">
                ⚠ This listing will be hidden from the browse board.
              </div>
            )}
          </section>
        )}

        {/* Notes */}
        <section>
          <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400 mb-4">Notes</h2>
          <textarea data-testid="sell-notes" className="h57-input min-h-[100px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Corners, centering, story — whatever helps the buyer trust the card." />
        </section>

        <PaymentDisclaimer compact />

        <div className="flex gap-3">
          <button type="button" onClick={() => navigate(-1)} className="h57-btn-secondary flex-1" data-testid="sell-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="h57-btn-primary flex-1" data-testid="sell-submit">
            {saving ? (isEdit ? "Saving…" : "Posting…") : (isEdit ? "Save Changes" : "Post Listing")}
          </button>
          {isEdit && nextId && (
            <button
              type="button"
              onClick={(e) => submit(e, { goNext: true })}
              disabled={saving}
              className="h57-btn-secondary flex-1"
              data-testid="sell-submit-next"
            >
              {saving ? "Saving…" : "Save & Next →"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
